import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  Plus,
  CalendarClock,
  Play,
  Pause,
  Edit2,
  Trash2,
  Zap,
  Clock,
  Layers
} from 'lucide-react';
import { blogService, ContentSchedule } from '../../services/blogService';
import BrandContextBanner from '../shared/BrandContextBanner';
import VariationControls from '../shared/VariationControls';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import AiCostBadge from '../ai/AiCostBadge';

type Frequency = 'daily' | 'weekly' | 'biweekly' | 'monthly';

interface RoutineForm {
  name: string;
  social_platforms: string[];
  frequency: Frequency;
  days_of_week: number[];
  time_of_day: string;
  posts_per_run: number;
  topics: string;
  tone: string;
  ignore_pattern: boolean;
  variation_direction: string;
  auto_publish: boolean;
}

const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'linkedin', 'twitter', 'tiktok', 'youtube'] as const;

const PLATFORM_LABELS: Record<string, string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  twitter: 'X',
  tiktok: 'TikTok',
  youtube: 'YouTube'
};

const emptyForm = (): RoutineForm => ({
  name: '',
  social_platforms: [],
  frequency: 'weekly',
  days_of_week: [1],
  time_of_day: '09:00',
  posts_per_run: 7,
  topics: '',
  tone: 'professional',
  ignore_pattern: false,
  variation_direction: '',
  auto_publish: false
});

const SocialAutoRoutine: React.FC = () => {
  const { t } = useTranslation();
  const [schedules, setSchedules] = useState<ContentSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<RoutineForm>(emptyForm());

  const frequencyLabels: Record<Frequency, string> = {
    daily: t('social.routine.freqDaily', 'Diário'),
    weekly: t('social.routine.freqWeekly', 'Semanal'),
    biweekly: t('social.routine.freqBiweekly', 'Quinzenal'),
    monthly: t('social.routine.freqMonthly', 'Mensal')
  };

  const weekdayLabels = [
    t('social.routine.dow.sun', 'Dom'),
    t('social.routine.dow.mon', 'Seg'),
    t('social.routine.dow.tue', 'Ter'),
    t('social.routine.dow.wed', 'Qua'),
    t('social.routine.dow.thu', 'Qui'),
    t('social.routine.dow.fri', 'Sex'),
    t('social.routine.dow.sat', 'Sáb')
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await blogService.listSchedules();
      const all = res?.data || [];
      setSchedules(all.filter((s) => Array.isArray(s.channels) && s.channels.includes('social')));
    } catch {
      setSchedules([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setModalOpen(true);
  };

  const openEdit = (s: ContentSchedule) => {
    setEditingId(s.id);
    setForm({
      name: s.name || '',
      social_platforms: Array.isArray(s.social_platforms) ? s.social_platforms : [],
      frequency: (s.frequency as Frequency) || 'weekly',
      days_of_week: Array.isArray(s.days_of_week) ? s.days_of_week : [],
      time_of_day: s.time_of_day || '09:00',
      posts_per_run: s.posts_per_run || 1,
      topics: Array.isArray(s.topics) ? s.topics.join('\n') : '',
      tone: s.tone || 'professional',
      ignore_pattern: s.metadata?.ignore_pattern ?? s.ignore_pattern ?? false,
      variation_direction: s.metadata?.variation_direction ?? s.variation_direction ?? '',
      auto_publish: !!s.auto_publish
    });
    setModalOpen(true);
  };

  const togglePlatform = (p: string) => {
    setForm((f) => ({
      ...f,
      social_platforms: f.social_platforms.includes(p)
        ? f.social_platforms.filter((x) => x !== p)
        : [...f.social_platforms, p]
    }));
  };

  const toggleDay = (d: number) => {
    setForm((f) => ({
      ...f,
      days_of_week: f.days_of_week.includes(d)
        ? f.days_of_week.filter((x) => x !== d)
        : [...f.days_of_week, d].sort((a, b) => a - b)
    }));
  };

  const showWeekdays = form.frequency === 'weekly' || form.frequency === 'biweekly';

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('social.routine.errName', 'Dê um nome à rotina.'));
      return;
    }
    if (form.social_platforms.length === 0) {
      toast.error(t('social.routine.errPlatforms', 'Selecione ao menos uma rede social.'));
      return;
    }
    setSaving(true);
    try {
      const payload: Partial<ContentSchedule> = {
        name: form.name.trim(),
        channels: ['social'],
        social_platforms: form.social_platforms,
        frequency: form.frequency,
        days_of_week: showWeekdays ? form.days_of_week : [],
        time_of_day: form.time_of_day,
        posts_per_run: Number(form.posts_per_run) || 1,
        topics: form.topics.split('\n').map((s) => s.trim()).filter(Boolean),
        tone: form.tone,
        ignore_pattern: form.ignore_pattern,
        variation_direction: form.variation_direction.trim() || undefined,
        auto_publish: form.auto_publish
      };
      if (editingId) {
        await blogService.updateSchedule(editingId, payload);
        toast.success(t('social.routine.updated', 'Rotina atualizada.'));
      } else {
        await blogService.createSchedule(payload);
        toast.success(t('social.routine.created', 'Rotina criada.'));
      }
      setModalOpen(false);
      await load();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || t('social.routine.saveError', 'Não foi possível salvar a rotina.'));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (s: ContentSchedule) => {
    const next = s.status === 'active' ? 'paused' : 'active';
    try {
      await blogService.updateSchedule(s.id, { status: next });
      toast.success(next === 'active'
        ? t('social.routine.activated', 'Rotina ativada.')
        : t('social.routine.paused', 'Rotina pausada.'));
      await load();
    } catch {
      toast.error(t('social.routine.statusError', 'Não foi possível alterar o status.'));
    }
  };

  const runNow = async (s: ContentSchedule) => {
    try {
      await blogService.runScheduleNow(s.id);
      toast.success(t('social.routine.ranNow', 'Geração iniciada. Os posts aparecerão em instantes.'));
      await load();
    } catch {
      toast.error(t('social.routine.runError', 'Não foi possível rodar agora.'));
    }
  };

  const remove = async (s: ContentSchedule) => {
    if (!window.confirm(t('social.routine.confirmDelete', 'Excluir esta rotina? Esta ação não pode ser desfeita.'))) return;
    try {
      await blogService.deleteSchedule(s.id);
      toast.success(t('social.routine.deleted', 'Rotina excluída.'));
      await load();
    } catch {
      toast.error(t('social.routine.deleteError', 'Não foi possível excluir a rotina.'));
    }
  };

  const toneOptions = [
    { value: 'professional', label: t('social.tones.professional', 'Profissional') },
    { value: 'casual', label: t('social.tones.casual', 'Casual') },
    { value: 'friendly', label: t('social.tones.friendly', 'Amigável') },
    { value: 'humorous', label: t('social.tones.humorous', 'Humor') },
    { value: 'inspirational', label: t('social.tones.inspirational', 'Inspirador') }
  ];

  const frequencyOptions = (Object.keys(frequencyLabels) as Frequency[]).map((f) => ({
    value: f,
    label: frequencyLabels[f]
  }));

  return (
    <div className="rounded-2xl border border-purple-200 dark:border-purple-900/40 bg-gradient-to-br from-purple-50/60 to-pink-50/40 dark:from-gray-900 dark:to-gray-900 p-5 sm:p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="p-2.5 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 shadow-lg shadow-purple-500/30 shrink-0">
            <CalendarClock className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('social.routine.title', 'Geração automática (recorrente)')}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              {t('social.routine.subtitle', 'Crie uma rotina que gera seus posts sozinha — ex.: toda segunda, gera a semana toda.')}
            </p>
          </div>
        </div>
        <Button variant="primary" onClick={openCreate} className="shrink-0 gap-2 self-start">
          <Plus className="h-4 w-4" />
          {t('social.routine.new', 'Nova rotina')}
        </Button>
      </div>

      <div className="mb-4">
        <BrandContextBanner />
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="h-32 rounded-xl bg-gray-100 dark:bg-gray-800 animate-pulse" />
          ))}
        </div>
      ) : schedules.length === 0 ? (
        <div className="text-center py-10 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 bg-white/60 dark:bg-gray-800/40">
          <CalendarClock className="h-10 w-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-900 dark:text-white">
            {t('social.routine.emptyTitle', 'Nenhuma rotina ainda')}
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-4">
            {t('social.routine.emptyDesc', 'Crie uma rotina para a IA publicar por você automaticamente.')}
          </p>
          <Button variant="primary" onClick={openCreate} className="gap-2">
            <Plus className="h-4 w-4" />
            {t('social.routine.new', 'Nova rotina')}
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {schedules.map((s) => (
            <Card key={s.id} padding={false} className="p-4">
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="min-w-0">
                  <h4 className="font-semibold text-gray-900 dark:text-white truncate">{s.name}</h4>
                  <div className="flex flex-wrap gap-1 mt-1.5">
                    {(s.social_platforms || []).map((p) => (
                      <Badge key={p} variant="primary" size="sm">
                        {PLATFORM_LABELS[p] || p}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Badge variant={s.status === 'active' ? 'success' : 'gray'} size="sm">
                  {s.status === 'active'
                    ? t('social.routine.statusActive', 'Ativo')
                    : t('social.routine.statusPaused', 'Pausado')}
                </Badge>
              </div>

              <div className="space-y-1.5 text-xs text-gray-500 dark:text-gray-400 mb-3">
                <div className="flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5" />
                  <span>{frequencyLabels[(s.frequency as Frequency)] || s.frequency}</span>
                  <span>·</span>
                  <span>{s.time_of_day}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5" />
                  <span>
                    {t('social.routine.postsPerRun', '{{count}} posts por execução', { count: s.posts_per_run })}
                  </span>
                </div>
                {s.next_run_at && (
                  <div className="flex items-center gap-1.5">
                    <CalendarClock className="h-3.5 w-3.5" />
                    <span>
                      {t('social.routine.nextRun', 'Próxima')}: {new Date(s.next_run_at).toLocaleString('pt-BR')}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center gap-1.5 pt-3 border-t border-gray-100 dark:border-gray-700">
                <Button variant="secondary" size="sm" onClick={() => toggleStatus(s)} className="gap-1.5">
                  {s.status === 'active' ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                  {s.status === 'active'
                    ? t('social.routine.pause', 'Pausar')
                    : t('social.routine.activate', 'Ativar')}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => runNow(s)} className="gap-1.5" title={t('social.routine.runNow', 'Rodar agora')}>
                  <Zap className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">{t('social.routine.runNow', 'Rodar agora')}</span>
                </Button>
                <AiCostBadge feature="content-schedule" unitLabel={t('aiCredits.perRound', 'por rodada')} className="hidden md:flex" />
                <div className="flex-1" />
                <button
                  onClick={() => openEdit(s)}
                  className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title={t('common.edit', 'Editar')}
                >
                  <Edit2 className="h-4 w-4" />
                </button>
                <button
                  onClick={() => remove(s)}
                  className="p-2 rounded-lg text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  title={t('common.delete', 'Excluir')}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId
          ? t('social.routine.editTitle', 'Editar rotina')
          : t('social.routine.createTitle', 'Nova rotina')}
        size="lg"
        maxHeight
      >
        <div className="space-y-5">
          <Input
            label={t('social.routine.nameLabel', 'Nome da rotina')}
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder={t('social.routine.namePlaceholder', 'Ex.: Posts da semana') as string}
          />

          <div>
            <label className="label">{t('social.routine.platformsLabel', 'Redes sociais')}</label>
            <div className="flex flex-wrap gap-2">
              {SOCIAL_PLATFORMS.map((p) => {
                const active = form.social_platforms.includes(p);
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => togglePlatform(p)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                      active
                        ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {PLATFORM_LABELS[p]}
                  </button>
                );
              })}
            </div>
          </div>

          <Select
            label={t('social.routine.frequencyLabel', 'Frequência')}
            options={frequencyOptions}
            value={form.frequency}
            onChange={(e) => setForm((f) => ({ ...f, frequency: e.target.value as Frequency }))}
          />

          {showWeekdays && (
            <div>
              <label className="label">{t('social.routine.daysLabel', 'Dias da semana')}</label>
              <div className="flex flex-wrap gap-2">
                {weekdayLabels.map((label, idx) => {
                  const active = form.days_of_week.includes(idx);
                  return (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => toggleDay(idx)}
                      className={`w-12 h-10 rounded-lg text-sm font-medium transition-all ${
                        active
                          ? 'bg-gradient-to-r from-purple-600 to-pink-500 text-white shadow-md'
                          : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">{t('social.routine.timeLabel', 'Horário')}</label>
              <input
                type="time"
                value={form.time_of_day}
                onChange={(e) => setForm((f) => ({ ...f, time_of_day: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="label">{t('social.routine.perRunLabel', 'Posts por execução')}</label>
              <div className="flex items-center gap-1.5 flex-wrap">
                {[1, 2, 3, 4, 5, 6, 7].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, posts_per_run: n }))}
                    className={`w-9 h-9 rounded-lg text-sm font-bold transition-all ${
                      form.posts_per_run === n
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {t('social.routine.perRunHint', 'ex.: 7 = um post por dia na semana')}
              </p>
            </div>
          </div>

          <Textarea
            label={t('social.routine.topicsLabel', 'Temas (um por linha)')}
            value={form.topics}
            onChange={(e) => setForm((f) => ({ ...f, topics: e.target.value }))}
            placeholder={t('social.routine.topicsPlaceholder', 'Dicas de produtividade\nBastidores\nLançamentos') as string}
            className="min-h-[96px]"
          />

          <Select
            label={t('social.routine.toneLabel', 'Tom')}
            options={toneOptions}
            value={form.tone}
            onChange={(e) => setForm((f) => ({ ...f, tone: e.target.value }))}
          />

          <div className="rounded-xl border border-gray-200 dark:border-gray-700 p-3">
            <VariationControls
              ignorePattern={form.ignore_pattern}
              onIgnoreChange={(v) => setForm((f) => ({ ...f, ignore_pattern: v }))}
              variationDirection={form.variation_direction}
              onVariationChange={(v) => setForm((f) => ({ ...f, variation_direction: v }))}
            />
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.auto_publish}
              onChange={(e) => setForm((f) => ({ ...f, auto_publish: e.target.checked }))}
              className="mt-0.5 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
            />
            <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
              {t('social.routine.autoPublish', 'Publicar automaticamente, sem revisão')}
            </span>
          </label>

          <p className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/60 rounded-lg p-3">
            {t('social.routine.weekNote', 'Roda no dia escolhido e distribui os posts pelos próximos dias.')}
          </p>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              {t('common.cancel', 'Cancelar')}
            </Button>
            <Button variant="primary" onClick={handleSave} loading={saving}>
              {editingId
                ? t('social.routine.save', 'Salvar')
                : t('social.routine.create', 'Criar rotina')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SocialAutoRoutine;
